import { Add, ArchiveOutlined, Search, UnarchiveOutlined } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import PrintersOverviewPanel from '../../components/printers/PrintersOverviewPanel';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import ViewModeToggle from '../../components/ViewModeToggle';
import {
    PRINTERS_LIST_VIEW_KEY,
    readViewMode,
} from '../../helpers/viewModePreference';

function Printers() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [draftSearchTerm, setDraftSearchTerm] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [archivedTotal, setArchivedTotal] = useState(0);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [viewMode, setViewMode] = useState(() => readViewMode(PRINTERS_LIST_VIEW_KEY));
    const [listVersion, setListVersion] = useState(0);

    const handleSearch = () => {
        setAppliedSearchQuery(String(draftSearchTerm || '').trim());
    };

    const handleClearSearch = () => {
        setDraftSearchTerm('');
        setAppliedSearchQuery('');
    };

    const handleCreatePrinter = () => {
        navigate('/admin/register-printer');
    };

    const handleEditPrinter = (id) => {
        navigate(`/admin/edit-printer/${id}`);
    };

    const handlePrinterMaintenances = (id) => {
        navigate(`/admin/printer-maintenances?printer_uuid=${id}`);
    };

    const handleCopyUuid = async (uuid) => {
        try {
            await navigator.clipboard.writeText(uuid);
            Swal.fire({
                title: 'Copiado!',
                text: 'UUID copiado. Use-o no Acompanha 3D Farm Link para sincronizar esta impressora.',
                icon: 'success',
                timer: 2200,
                showConfirmButton: false,
            });
        } catch {
            Swal.fire({
                title: 'Não foi possível copiar',
                text: 'Selecione o UUID manualmente e use Ctrl+C (ou Cmd+C).',
                icon: 'warning',
            });
        }
    };

    const handleDeletePrinter = (printerId) => {
        return Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta impressora será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (!result.isConfirmed || loadingDelete) {
                return false;
            }
            setLoadingDelete(true);
            return api.delete(`printers/${printerId}`)
                .then(() => {
                    Swal.fire('Removido!', 'A impressora foi removida com sucesso.', 'success');
                    setListVersion((v) => v + 1);
                    return true;
                })
                .catch(() => {
                    Swal.fire('Erro!', 'Houve um problema ao remover a impressora.', 'error');
                    return false;
                })
                .finally(() => {
                    setLoadingDelete(false);
                });
        });
    };

    const handleArchivedTotalChange = useCallback((total) => {
        setArchivedTotal(Number(total) || 0);
    }, []);

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Impressoras</Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    mb: 2,
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 1.5,
                        flex: 1,
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar impressora..."
                        value={draftSearchTerm}
                        onChange={(e) => setDraftSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        fullWidth={isMobile}
                        sx={{ maxWidth: isMobile ? undefined : 320 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchQuery && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar
                        </Button>
                    )}
                    <ViewModeToggle
                        value={viewMode}
                        onChange={setViewMode}
                        storageKey={PRINTERS_LIST_VIEW_KEY}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 1,
                        flexShrink: 0,
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={showArchived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
                        onClick={() => setShowArchived((prev) => !prev)}
                        fullWidth={isMobile}
                    >
                        {showArchived
                            ? 'Ver ativas'
                            : `Ver arquivadas${archivedTotal > 0 ? ` (${archivedTotal})` : ''}`}
                    </Button>
                    <PageTutorialButtons pageKey="printers" fullWidth={isMobile} />
                    <QuotaLimitedButton
                        quotaKey="printers"
                        variant="contained"
                        color="primary"
                        onClick={handleCreatePrinter}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Nova impressora
                    </QuotaLimitedButton>
                </Box>
            </Box>

            {showArchived && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Impressoras inativas ficam aqui. Elas não entram nos cards de produção.
                </Typography>
            )}

            <PrintersOverviewPanel
                key={listVersion}
                viewMode={viewMode}
                showArchived={showArchived}
                searchQuery={appliedSearchQuery}
                onArchivedTotalChange={handleArchivedTotalChange}
                onEdit={handleEditPrinter}
                onMaintenances={handlePrinterMaintenances}
                onDelete={handleDeletePrinter}
                onCopyUuid={handleCopyUuid}
            />
        </div>
    );
}

export default Printers;
